import { Box, useToken } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaTruck } from 'react-icons/fa';
import { IoEyeOutline } from 'react-icons/io5';
import { MdAssignment, MdSupportAgent } from 'react-icons/md';
import { useSearchParams } from 'react-router';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import ShipmentModalContent from '@/components/Templates/Requests/RequestsTable/ShipmentModalContent';
import {
	DemoReturnForm,
	OrderReturnDetails,
	ReturnOrderForm,
} from '@/components/Templates/Returns';
import type { DemoReturnFormHandle } from '@/components/Templates/Returns/ReturnOrderForm/DemoReturnForm';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import { allowedPortForProducts } from '@/constants/clinic-port';
import { useGetProductClinicPort } from '@/hooks/service-hooks/common';
import {
	useGetOrderReturns,
	useGetReturnProducts,
} from '@/hooks/service-hooks/orderReturns';
import { useGetReturnOrderById } from '@/hooks/service-hooks/returns';
import { useReturnsActions } from '@/hooks/tutorialsHooks/useReturnsActions';
import { useReturnsTutorial } from '@/hooks/tutorialsHooks/useReturnsTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useAppTour } from '@/hooks/useAppTour';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getOrderReturns } from '@/services/orderReturns';
import { useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import type { FilterType } from '@/types/table';
import { trackCoreClick } from '@/utils/analytics';
import { formatDate, parseUserFriendlyError } from '@/utils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockReturnData } from '@/utils/tutorials/mockData';
import {
	columns,
	getOrderReturnStatus,
	statusFilterOptions,
} from './orderReturns';


const CLINIC_KEY = '_ta_customername_value';
const PRODUCT_KEY = 'product.ta_qcode';
const OrderReturnsTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();

	// Shared clinic filter helpers
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(
		CLINIC_KEY,
	);

	const user = useAuthStore((state) => state.user?.userInformation);
	const ENABLE_PRODUCT_BY_PORT = ['SalesRep', 'Mindsight'].includes(
		user?.portsAvailable || '',
	);

	// Modal state for create form
	const [modalInformation, setModalInformation] = useState<{
		mode: string;
	} | null>({
		mode: '',
	});

	// Modal title state for dynamic title changes
	const [modalTitle, setModalTitle] = useState('Create Order Return');

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] = useState(false);
	const [returnExitDraftAction, setReturnExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	// State for selected return order actions
	const [selectedReturn, setSelectedReturn] = useState<{
		action:
		| 'details'
		| 'trackingInfo'
		| 'viewShipmentDetails'
		| 'viewOrderRequest'
		| 'supportTicket'
		| null;
		data: string | null;
		clinicData?: { value: string; label: string } | null;
		orderName?: string; // For support ticket subject
	}>({
		action: null,
		data: null,
	});

	const [products, setProducts] = useState<{ id: string; label: string }[]>([]);
	// Fetch products available for user's port and selected clinics
	const { data: portProductData } = useGetProductClinicPort({
		clinicIds: defaultClinicIds,
		port: user?.portsAvailable || '',
		enabled: ENABLE_PRODUCT_BY_PORT,
	});

	// Initialize with DataTable's default values and clinic filters from store
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
				limit: 15,
				columnsFilters: '[]',
				search: '',
				enabled: defaultClinicIds.length > 0 &&
					(!!portProductData || !ENABLE_PRODUCT_BY_PORT),
				status: '',
				globalFilters: JSON.stringify(
					[
						...(defaultClinicIds.length ? [{
							key: CLINIC_KEY,
							values: defaultClinicIds,
						}] : []),
						...(ENABLE_PRODUCT_BY_PORT && portProductData?.length ? [{
							key: PRODUCT_KEY,
							values:
								portProductData?.flatMap((p) =>
									(
										p[
										allowedPortForProducts.ConsignmentOrder.by as keyof typeof p
										] as string
									)?.split(','),
								) || [],
						}] : []),
					]
				),
				sort: '',
	});

	const { data, isFetching, refetch } = useGetOrderReturns(filters);

	// This function fetches products based on selected clinics
	const {
		data: returnProductsData,
		isFetching: isFetchingReturnProducts,
		refetch: refetchReturnProducts,
	} = useGetReturnProducts({ 
		...filters,
		globalFilters: ENABLE_PRODUCT_BY_PORT
			? JSON.stringify([
					...JSON.parse(filters.globalFilters || '[]'),
					{
						key: PRODUCT_KEY,
						values:
							portProductData?.flatMap((p) =>
								(
									p[
										allowedPortForProducts.OrderReturn.by as keyof typeof p
									] as string
								)?.split(','),
							) || [],
					},
				])
			: filters.globalFilters,
		limit: 9999,
		enabled: defaultClinicIds.length > 0 && 
			products?.length === 0 && (!!portProductData || !ENABLE_PRODUCT_BY_PORT),
	});

	useEffect(() => {
		if (returnProductsData?.items && !products.length) {
			setProducts(
				returnProductsData.items.map((item) => ({
					id: item.qCode,
					label: item.productName + ' (' + item.qCode + ')',
				})),
			);
			
			const productCodes = ENABLE_PRODUCT_BY_PORT && portProductData?.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

			// Update filters state to trigger API call
			setFilters({
				...filters,
				enabled: defaultClinicIds.length > 0 && (!!portProductData || !ENABLE_PRODUCT_BY_PORT),
				globalFilters: JSON.stringify([
					{
						key: CLINIC_KEY,
						values: defaultClinicIds,
					},
					...(ENABLE_PRODUCT_BY_PORT && productCodes.length ? [{
						key: PRODUCT_KEY,
						values: productCodes,
					}] : []),
				])
			});
		}
	}, [returnProductsData, products.length]);

	// Refs for Returns table (for tutorial)
	const returnsTableRef = useRef<HTMLDivElement | null>(null);
	const addButtonReturnsTableRef = useRef<HTMLButtonElement | null>(null);
	const returnsActionColumnRef = useRef<HTMLElement | null>(null);

	// Demo modal state (for tutorial)
	const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
	const demoFormRef = useRef<DemoReturnFormHandle | null>(null);
	const isClosingFromTutorialAction = useRef(false);
	const {
		isReturnsTutorialActive: isReturnsTutorialWCActive,
		isOnboardingActive,
	} = useTutorialActive();
	const { activeTutorialName } = useTourContext();

	const isAnyReturnsTutorialActive =
		isReturnsTutorialWCActive ||
		(isOnboardingActive &&
			(activeTutorialName === 'OnboardingMindsightTutorial' ||
				activeTutorialName === 'OnboardingWCTutorial' ||
				activeTutorialName === 'OnboardingSalesRepTutorial'));

	// Use the Returns Tutorial hook to manage all tutorial logic
	useReturnsTutorial({
		returnsTableRef,
		returnsActionColumnRef,
	});

	// Use Returns Actions hook to handle tutorial actions
	useReturnsActions({
		openCreateReturnModal: () => {
			setIsDemoModalOpen(true);
		},
		goToReturnStep: (step: number) => {
			demoFormRef.current?.goToStep(step);
		},
		onCloseDemoReturnModal: () => {
			isClosingFromTutorialAction.current = true;
			setIsDemoModalOpen(false);
			setTimeout(() => {
				isClosingFromTutorialAction.current = false;
			}, 100);
		},
	});

	// Register tutorial element refs using useAppTour
	useAppTour({
		elements: {
			ReturnsTableRef: returnsTableRef.current,
			ReturnsAddButtonRef: addButtonReturnsTableRef.current,
			ReturnsActionColumnRef: returnsActionColumnRef.current,
		},
	});

	// Check URL params for return ID to open modal
	const [searchParams, setSearchParams] = useSearchParams();
	const returnIdFromUrl = searchParams.get('returnId');

	// Open modal if returnId is in URL
	useEffect(() => {
		if (returnIdFromUrl && !selectedReturn.data) {
			setSelectedReturn({ action: 'details', data: returnIdFromUrl });
			// Remove the query parameter from URL after opening modal
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('returnId');
			setSearchParams(newSearchParams, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [returnIdFromUrl]);

	// Fetch return order details when a modal is open
	const { data: returnOrderData, isLoading: isLoadingReturnOrder } =
		useGetReturnOrderById(
			selectedReturn.data || '',
			!!selectedReturn.data &&
			selectedReturn.action !== 'details' &&
			selectedReturn.action !== null,
		);

	// Safely extract return order details
	const returnOrderDetails = returnOrderData?.data;
	const hasReturnOrderData = !isLoadingReturnOrder && !!returnOrderDetails;

	// Transform API data to table format
	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isAnyReturnsTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockReturnData(10);
			return mockData.map((item) => {
				const status = getOrderReturnStatus(item.ta_status || 0);
				const inventories = item.inventories || [];
				const qty = inventories.length;

				// Product display logic: show "Multiple" if more than 1, else show product name
				let productDisplay = '-';
				if (qty > 1) {
					productDisplay = 'Multiple Products';
				} else if (qty === 1 && inventories[0]?.product) {
					const productName = inventories[0].product.ta_name || '-';
					const productQCode = inventories[0].product.ta_qcode;
					productDisplay = productQCode
						? `${productName} (${productQCode})`
						: productName;
				}

				// Shipping location: ta_streetaddress - ta_city
				const shippingLocation =
					item.ta_streetaddress && item.ta_city
						? `${item.ta_streetaddress} - ${item.ta_city}`
						: item.ta_streetaddress || item.ta_city || '-';

				const clinicName = item.clinic?.name || '-';

				return {
					ta_returnrequestid: item.ta_returnrequestid,
					id: item.ta_returnrequestid,
					_statusCode: item.ta_status, // For action filtering
					_hasInventories: inventories.length > 0,
					_hasOrderRequest: !!item.orderRequest,
					ta_expirationdate: formatDate(item.ta_expirationdate),
					ta_datereturnedtransferred: formatDate(item.ta_datereturnedtransferred),
					ta_newshippingdaterequested: formatDate(
						item.ta_newshippingdaterequested,
					),
					'product.ta_name': (
						<FaceliftTruncatedTextWithTooltip
							text={productDisplay}
							maxLength={40}
						/>
					),
					qty: qty,
					shipping_location: (
						<FaceliftTruncatedTextWithTooltip
							text={shippingLocation}
							maxLength={35}
						/>
					),
					'clinic.name': (
						<FaceliftTruncatedTextWithTooltip text={clinicName} maxLength={30} />
					),
					ta_status: <Box
						bg={status.color}
						borderRadius={4}
						color="white"
						height="24px"
						px={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight={500}
						fontSize="sm"
					>
						<FaceliftTruncatedTextWithTooltip
							text={status.label}
							maxLength={20}
						/>
					</Box>,
				};
			});
		}

		if (!data?.items) return [];

		return data.items.map((item) => {
			const status = getOrderReturnStatus(item.ta_status || 0);
			const inventories = item.inventories || [];
			const qty = inventories.length;

			// Product display logic: show "Multiple" if more than 1, else show product name
			let productDisplay = '-';
			if (qty > 1) {
				productDisplay = 'Multiple Products';
			} else if (qty === 1 && inventories[0]?.product) {
				const productName = inventories[0].product.ta_name || '-';
				const productQCode = inventories[0].product.ta_qcode;
				productDisplay = productQCode
					? `${productName} (${productQCode})`
					: productName;
			}

			// Shipping location: ta_streetaddress - ta_city
			const shippingLocation =
				item.ta_streetaddress && item.ta_city
					? `${item.ta_streetaddress} - ${item.ta_city}`
					: item.ta_streetaddress || item.ta_city || '-';

			const clinicName = item.clinic?.name || '-';

			return {
				ta_returnrequestid: item.ta_returnrequestid,
				id: item.ta_returnrequestid,
				_statusCode: item.ta_status, // For action filtering
				_hasInventories: inventories.length > 0,
				_hasOrderRequest: !!item.orderRequest,
				ta_expirationdate: formatDate(item.ta_expirationdate),
				ta_datereturnedtransferred: formatDate(item.ta_datereturnedtransferred),
				ta_newshippingdaterequested: formatDate(
					item.ta_newshippingdaterequested,
				),
				'product.ta_name': (
					<FaceliftTruncatedTextWithTooltip
						text={productDisplay}
						maxLength={40}
					/>
				),
				qty: qty,
				shipping_location: (
					<FaceliftTruncatedTextWithTooltip
						text={shippingLocation}
						maxLength={35}
					/>
				),
				'clinic.name': (
					<FaceliftTruncatedTextWithTooltip text={clinicName} maxLength={30} />
				),
				ta_status: <Box
					bg={status.color}
					borderRadius={4}
					color="white"
					height="24px"
					px={3}
					display="flex"
					alignItems="center"
					justifyContent="center"
					fontWeight={500}
					fontSize="sm"
				>
					<FaceliftTruncatedTextWithTooltip
						text={status.label}
						maxLength={20}
					/>
				</Box>,
			};
		});
	}, [data, isAnyReturnsTutorialActive]);

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {

		const globalFilter = mapGlobalFilters(globalFilters)
		const productGlobalFilter = globalFilter.find(
			(filter) => filter.key === PRODUCT_KEY,
		);
		const productValuesFromFilter = productGlobalFilter
			? productGlobalFilter.values
			: [];

		// If product filter is applied, use those values
		// Otherwise, fall back to port-based products if enabled
		const portBasedProductValues =
			ENABLE_PRODUCT_BY_PORT && portProductData?.length
				? portProductData[0].portProducts?.split(',').filter(Boolean) || []
				: [];

		const finalProductValues =
			productValuesFromFilter.length > 0
				? productValuesFromFilter
				: portBasedProductValues;

		const clinicGlobalFilter = globalFilter.find(
			(filter) => filter.key === CLINIC_KEY,
		);
		
		const clinicValuesFromFilter = clinicGlobalFilter
			? clinicGlobalFilter.values
			: [];
		const finalClinicIds = clinicValuesFromFilter.length > 0
			? clinicValuesFromFilter
			: defaultClinicIds;

		// Add the final clinic and product filters in setFilters
		const productCodes = finalProductValues;
		const finalGlobalFilters = [
			{
				key: CLINIC_KEY,
				values: finalClinicIds,
			},
			...(productCodes.length ? [{
				key: PRODUCT_KEY,
				values: productCodes,
			}] : []),
		];

		// Update filters state to trigger API call
		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: status?.value || '',
			globalFilters: JSON.stringify(finalGlobalFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Transform data for export (without JSX elements)
	const transformedDataToExport = (items: any[]) => {
		if (!items) return [];

		return items.map((item) => {
			const status = getOrderReturnStatus(item.ta_status || 0);
			const inventories = item.inventories || [];
			const qty = inventories.length;

			// Product display logic: show "Multiple" if more than 1, else show product name
			let productDisplay = '-';
			if (qty > 1) {
				productDisplay = 'Multiple Products';
			} else if (qty === 1 && inventories[0]?.product) {
				const productName = inventories[0].product.ta_name || '-';
				const productQCode = inventories[0].product.ta_qcode;
				productDisplay = productQCode
					? `${productName} (${productQCode})`
					: productName;
			}

			// Shipping location: ta_streetaddress - ta_city
			const shippingLocation =
				item.ta_streetaddress && item.ta_city
					? `${item.ta_streetaddress} - ${item.ta_city}`
					: item.ta_streetaddress || item.ta_city || '-';

			const clinicName = item.clinic?.name || '-';

			return {
				ta_returnrequestid: item.ta_returnrequestid,
				id: item.ta_returnrequestid,
				ta_expirationdate: formatDate(item.ta_expirationdate),
				ta_datereturnedtransferred: formatDate(item.ta_datereturnedtransferred),
				ta_newshippingdaterequested: formatDate(
					item.ta_newshippingdaterequested,
				),
				'product.ta_name': productDisplay,
				qty: qty,
				shipping_location: shippingLocation,
				'clinic.name': clinicName,
				ta_status: status.label,
			};
		});
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const response = await getOrderReturns(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(response?.items || []);
			generateCSVFromTable(columns, transformedData, 'OrderReturns-all', true);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			const userFriendlyMessage = parseUserFriendlyError(
				error,
				'Unable to export data. Please try again.',
			);
			toast.error(userFriendlyMessage);
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const response = await getOrderReturns(
				1,
				9999,
				filters.columnsFilters,
				filters.search,
				String(filters.status || ''),
				filters.globalFilters,
				String(filters.sort || ''),
			);

			const transformedData = transformedDataToExport(response?.items || []);
			generateExcelFromTable(
				columns,
				transformedData,
				'OrderReturns-all',
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			const userFriendlyMessage = parseUserFriendlyError(
				error,
				'Unable to export data. Please try again.',
			);
			toast.error(userFriendlyMessage);
		}
	};

	const exportAllAsPDF = () => {
		console.log('Exporting all data as PDF');
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
		>
		<DataTable
			ref={returnsTableRef}
			tableName="Return Orders"
			moduleName="Return Order"
			addButtonRef={addButtonReturnsTableRef}
				loading={isFetching || isFetchingReturnProducts}
				items={transformedData}
				columns={columns}
				topLabel="Return Orders"
				totalCount={data?.meta.totalRecords || 0}
				onFilterApply={onFilterApply}
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				exportable
			bordered
			globalFilter={{
				title: 'Filters',
				clinics: true,
				items: [
					{
						name: 'Products',
						key: PRODUCT_KEY,
						options: products,
					},
				],
			}}
			{...(hasPermission(['Returns:Create']) && {
					creatableFn: () => {
						trackCoreClick('create_return_order_clicked', {
							source_module: 'order_returns_table',
						});
						setModalInformation({ mode: 'createReturn' });
					},
				})}
				statusFilters={statusFilterOptions}
				actions={[
					{
						label: 'Details',
						icon: <IoEyeOutline />,
						actionFn: (item: ColumnData) => {
							const returnId = item.ta_returnrequestid as string;
							if (returnId) {
								setSelectedReturn({ action: 'details', data: returnId });
							}
						},
					},
					{
						label: 'View Return Items',
						icon: <FaTruck />,
						actionFn: (item: ColumnData) => {
							const returnId = item.ta_returnrequestid as string;
							if (returnId) {
								setSelectedReturn({
									action: 'viewShipmentDetails',
									data: returnId,
								});
							}
						},
						show: (item: any) => item._hasInventories,
					},
					{
						label: 'View Order Status',
						icon: <MdAssignment />,
						actionFn: (item: ColumnData) => {
							const returnId = item.ta_returnrequestid as string;
							if (returnId) {
								setSelectedReturn({
									action: 'viewOrderRequest',
									data: returnId,
								});
							}
						},
						show: (item: any) => item._hasOrderRequest,
					},
				{
					label: 'Support Ticket',
					icon: <MdSupportAgent />,
					actionFn: (item: ColumnData) => {
						// Find the original data to get clinic information
						const originalItem = data?.items?.find(
							(ret: any) => ret.ta_returnrequestid === item.id,
						);
						const clinic = originalItem?.clinic;
						const orderName = originalItem?.ta_name || '';

						setIsSupportTicketFormDirty(false);
						setSelectedReturn({
							action: 'supportTicket',
							data: item.ta_returnrequestid as string,
							clinicData:
								clinic && clinic.accountid && clinic.name
									? { value: clinic.accountid, label: clinic.name }
									: null,
							orderName: orderName,
						});
					},
					show: () => hasPermission(['SupportTickets:Create']),
				},
				]}
				csv
				excel
				// pdf
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			// exportAllAsPDF={exportAllAsPDF}
			/>

			{/* Create Return Order Modal */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation?.mode === 'createReturn'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation(null);
						setModalTitle('Create Order Return');
					}
				}}
				title={modalTitle}
				showBackBtn={true}
				size="xl"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={returnExitDraftAction}
			>
				<ReturnOrderForm
					onSuccess={() => {
						// Close modal, reset title and refetch table data so the new return appears
						setModalInformation(null);
						setModalTitle('Create Order Return');
						refetch();
					}}
					onTitleChange={setModalTitle}
					onIsDirtyChange={setIsFormDirty}
					onExitConfirmationDraftActionChange={
						modalInformation?.mode === 'createReturn'
							? setReturnExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			{/* Return Order Details Modal */}
			<FaceliftDialog
				open={selectedReturn.action === 'details'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedReturn({ action: null, data: null });
					}
				}}
				title="Details"
				showBackBtn={true}
				size="xl"
				sx={{
					width: 980,
					maxWidth: '95vw',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<Show
					when={selectedReturn.action === 'details' && !!selectedReturn.data}
				>
					<OrderReturnDetails returnOrderId={selectedReturn.data!} />
				</Show>
			</FaceliftDialog>

			{/* Shipment Details Modal */}
			<FaceliftDialog
				open={selectedReturn.action === 'viewShipmentDetails'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedReturn({ action: null, data: null });
					}
				}}
				title="Return Items"
				showBackBtn={true}
				size="xl"
				sx={{
					width: 1000,
					maxWidth: '90vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<ShipmentModalContent
					orderData={returnOrderData}
					isLoading={isLoadingReturnOrder}
				/>
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={selectedReturn.action === 'supportTicket'}
				onOpenChange={(openState) => {
					if (!openState) {
						setSelectedReturn({ action: null, data: null });
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
			<SupportTicketForm
				mode="create"
				initialData={{
					clinic: selectedReturn.clinicData || null,
					type: { value: '953870002', label: 'Orders' },
					subject: selectedReturn.orderName || '',
					status: { value: '1', label: 'In Progress' },
				}}
				onSuccess={() => {
					setSelectedReturn({ action: null, data: null });
					setIsSupportTicketFormDirty(false);
					refetch();
				}}
				onIsDirtyChange={setIsSupportTicketFormDirty}
				onExitConfirmationDraftActionChange={
					selectedReturn.action === 'supportTicket'
						? setSupportTicketExitDraftAction
						: undefined
				}
			/>
			</FaceliftDialogWithExitConfirmation>
			{/* Demo Return Form Modal (for tutorial) */}
			<FaceliftDialog
				open={isDemoModalOpen}
				onOpenChange={(openState) => {
					if (
						!openState &&
						isAnyReturnsTutorialActive &&
						!isClosingFromTutorialAction.current
					) {
						return;
					}
					setIsDemoModalOpen(openState);
				}}
				showBackBtn={true}
				title="Returns"
				sx={{
					width: 900,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
				size="xl"
			>
				<DemoReturnForm ref={demoFormRef} />
			</FaceliftDialog>
		</Box>
	);
};

export default OrderReturnsTable;
